import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import AIAdvisor from "@/components/AIAdvisor";
import Home from "@/pages/Home";
import { FrameworksList, FrameworkDetail, NotFound } from "@/pages/Frameworks";
import { SingaporeHub, RegulatorDetail } from "@/pages/Singapore";
import MoneyFlow from "@/pages/MoneyFlow";
import Tools from "@/pages/Tools";
import ToolDetail from "@/pages/ToolDetail";
import Glossary from "@/pages/Glossary";
import { About, GenericPage, Contact } from "@/pages/Static";
import { BreachPlaybook, TemplatesLibrary, OverlapMatrix, MoneyFlowDetail } from "@/pages/DeepDives";
import { Login, Signup, ProtectedRoute, GoogleAuthCallback } from "@/pages/Auth";
import { Profile, Pricing, PricingSuccess, PricingCancel } from "@/pages/Account";
import AdminPanel from "@/pages/Admin";
import { SNEEREmbedPage, EmbedMarketing } from "@/pages/Embed";
import { WorkflowsLanding, WorkflowTemplateDetail, WorkflowRunOutput } from "@/pages/Workflows";
import Trending from "@/pages/Trending";
import { NewsletterList, NewsletterIssue } from "@/pages/Newsletter";
import Enterprise from "@/pages/Enterprise";
import Team, { AcceptInvite } from "@/pages/Team";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  const [advisorOpen, setAdvisorOpen] = useState(false);
  // Embed routes render bare (no nav, footer, advisor) so they can be iframed
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const isEmbed = path.startsWith("/embed/");
  if (isEmbed) {
    return (
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/embed/sneer" element={<SNEEREmbedPage />} />
            <Route path="*" element={<SNEEREmbedPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <NavBar onOpenAdvisor={() => setAdvisorOpen(true)} />
        <main>
          <Routes>
            <Route path="/" element={<Home onOpenAdvisor={() => setAdvisorOpen(true)} />} />

            <Route path="/frameworks" element={<FrameworksList />} />
            <Route path="/frameworks/:slug" element={<FrameworkDetail />} />

            <Route path="/singapore" element={<SingaporeHub />} />
            <Route path="/singapore/breach-playbook" element={<BreachPlaybook />} />
            <Route path="/singapore/templates" element={<TemplatesLibrary />} />
            <Route path="/singapore/overlap" element={<OverlapMatrix />} />
            <Route path="/singapore/:slug" element={<RegulatorDetail />} />

            <Route path="/money-flow" element={<MoneyFlow />} />
            <Route path="/money-flow/sneer" element={<MoneyFlow />} />
            <Route path="/money-flow/:slug" element={<MoneyFlowDetail />} />

            <Route path="/tools" element={<Tools />} />
            <Route path="/tools/:slug" element={<ToolDetail />} />

            <Route path="/glossary" element={<Glossary />} />

            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/partners" element={<GenericPage title="Partners" kicker="Partner programme" body="Co-marketing, content syndication and webinar sponsorship opportunities with RiskPedia Singapore." />} />
            <Route path="/press" element={<GenericPage title="Press" kicker="Media" body="Press inquiries, fact sheets, founder bios and high-resolution assets." />} />
            <Route path="/webinars" element={<GenericPage title="Singapore Risk Desk" kicker="Webinars · live monthly" body="A CRO, regulator or GRC consultant every month. Live attendance free. Recordings for Pro members." />} />
            <Route path="/shorts" element={<GenericPage title="RiskPedia Shorts" kicker="60-second explainers" body="PDPA, MAS Notices, MindForge — bite-sized explainers. Watch on the train." />} />
            <Route path="/community" element={<GenericPage title="Community Forum" kicker="Practitioner discussion" body="Admin-curated launch. WhatsApp Singapore Risk Alerts also available." />} />
            <Route path="/resources" element={<GenericPage title="Resources" kicker="Glossary · Webinars · Community" body="Choose your destination above, or jump straight to the glossary." />} />

            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/pricing/success" element={<PricingSuccess />} />
            <Route path="/pricing/cancel" element={<PricingCancel />} />
            <Route path="/admin" element={<ProtectedRoute admin><AdminPanel /></ProtectedRoute>} />

            <Route path="/embed" element={<EmbedMarketing />} />

            <Route path="/workflows" element={<WorkflowsLanding />} />
            <Route path="/workflows/templates/:tpl_id" element={<WorkflowTemplateDetail />} />
            <Route path="/workflows/runs/:run_id" element={<WorkflowRunOutput />} />

            <Route path="/trending" element={<Trending />} />

            <Route path="/newsletter" element={<NewsletterList />} />
            <Route path="/newsletter/:issue_id" element={<NewsletterIssue />} />

            <Route path="/enterprise" element={<Enterprise />} />
            <Route path="/team" element={<Team />} />
            <Route path="/team/accept/:token" element={<AcceptInvite />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <AIAdvisor open={advisorOpen} onClose={() => setAdvisorOpen(false)} />
      </BrowserRouter>
    </div>
  );
}

export default App;
